import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AppRole, Profile } from '@/types/database';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  isLoading: boolean;
  isAdmin: boolean;
  isStreamer: boolean;
  isAffiliate: boolean;
  isModerator: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const checkSuspended = async (userId: string): Promise<boolean> => {
    const { data } = await supabase
      .from('suspended_users')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    return !!data;
  };

  const forceLogout = async (message: string) => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    // Store message to show after redirect
    sessionStorage.setItem('auth_error', message);
    window.location.href = '/auth';
  };

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data);
  };

  const fetchRoles = async (userId: string) => {
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    setRoles(data?.map(r => r.role as AppRole) || []);
  };

  const refreshRoles = async () => {
    if (user) {
      await fetchRoles(user.id);
    }
  };

  useEffect(() => {
    let suspensionChannel: ReturnType<typeof supabase.channel> | null = null;
    let currentUserId: string | null = null;

    const setupSuspensionCheck = (userId: string) => {
      currentUserId = userId;
      
      // Check if already suspended
      supabase
        .from('suspended_users')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            console.log('User is suspended, forcing logout');
            supabase.auth.signOut().then(() => {
              setUser(null);
              setSession(null);
              setProfile(null);
              setRoles([]);
              sessionStorage.setItem('auth_error', 'Your account has been suspended. Please contact support.');
              window.location.href = '/auth';
            });
          }
        });

      // Set up real-time subscription for new suspensions
      if (!suspensionChannel) {
        console.log('Setting up suspension realtime channel for user:', userId);
        suspensionChannel = supabase
          .channel(`suspension-${userId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'suspended_users',
              filter: `user_id=eq.${userId}`,
            },
            (payload) => {
              console.log('User suspended in real-time:', payload);
              supabase.auth.signOut().then(() => {
                setUser(null);
                setSession(null);
                setProfile(null);
                setRoles([]);
                sessionStorage.setItem('auth_error', 'Your account has been suspended. Please contact support.');
                window.location.href = '/auth';
              });
            }
          )
          .subscribe((status) => {
            console.log('Suspension channel status:', status);
          });
      }
    };

    const cleanupSuspensionChannel = () => {
      if (suspensionChannel) {
        console.log('Cleaning up suspension channel');
        supabase.removeChannel(suspensionChannel);
        suspensionChannel = null;
      }
      currentUserId = null;
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer all Supabase calls to avoid deadlock
          setTimeout(() => {
            setupSuspensionCheck(session.user.id);
            fetchProfile(session.user.id);
            fetchRoles(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          cleanupSuspensionChannel();
        }
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setupSuspensionCheck(session.user.id);
        fetchProfile(session.user.id);
        fetchRoles(session.user.id);
      }
      setIsLoading(false);
    });

    // Also check suspension periodically (fallback)
    const intervalId = setInterval(() => {
      if (currentUserId) {
        supabase
          .from('suspended_users')
          .select('id')
          .eq('user_id', currentUserId)
          .maybeSingle()
          .then(({ data }) => {
            if (data) {
              console.log('Periodic check: User is suspended');
              supabase.auth.signOut().then(() => {
                setUser(null);
                setSession(null);
                setProfile(null);
                setRoles([]);
                sessionStorage.setItem('auth_error', 'Your account has been suspended. Please contact support.');
                window.location.href = '/auth';
              });
            }
          });
      }
    }, 5000); // Check every 5 seconds as fallback

    return () => {
      subscription.unsubscribe();
      cleanupSuspensionChannel();
      clearInterval(intervalId);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    // Check suspension before attempting login
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    
    if (profile) {
      const isSuspended = await checkSuspended(profile.id);
      if (isSuspended) {
        return { error: new Error('Your account has been suspended. Please contact support.') };
      }
    }
    
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { full_name: fullName }
      }
    });
    
    // Send custom verification email via Resend (Supabase auto-confirms, we handle verification in profile)
    if (!error && data.user) {
      try {
        await supabase.functions.invoke("send-verification-email", {
          body: {
            userId: data.user.id,
            email: email,
            fullName: fullName,
          },
        });
      } catch (emailError) {
        console.error("Failed to send verification email:", emailError);
        // Don't fail signup if email fails - user can request resend
      }
    }
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
  };

  const value = {
    user,
    session,
    profile,
    roles,
    isLoading,
    isAdmin: roles.includes('admin'),
    isStreamer: roles.includes('streamer'),
    isAffiliate: roles.includes('affiliate'),
    isModerator: roles.includes('moderator'),
    signIn,
    signUp,
    signOut,
    refreshRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
